import { action, computed, makeObservable, observable } from 'mobx';

export interface CardTheme {
  id: string;
  name: string;
  backgroundImage: string;
  backgroundBlendMode: string;
}

export interface FontTheme {
  id: string;
  name: string;
  description: string;
  editorial: string;
  mono: string;
}

export const FONT_THEMES: FontTheme[] = [
  {
    id: 'studio',
    name: 'Studio',
    description: 'Fraunces + DM Sans',
    editorial: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "'Space Mono', monospace",
  },
  {
    id: 'editorial',
    name: 'Editorial',
    description: 'IBM Plex Sans + Space Mono',
    editorial: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "'Space Mono', monospace",
  },
  {
    id: 'classic',
    name: 'Classic',
    description: 'Inter + JetBrains Mono',
    editorial: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "'JetBrains Mono', monospace",
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'System fonts',
    editorial: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'SF Mono', 'Menlo', 'Consolas', monospace",
  },
  {
    id: 'technical',
    name: 'Technical',
    description: 'Roboto family',
    editorial: "'Roboto', -apple-system, sans-serif",
    mono: "'Roboto Mono', monospace",
  },
];

export const CARD_THEMES: CardTheme[] = [
  {
    id: 'amber-gold',
    name: 'Amber Gold',
    backgroundImage: `
      radial-gradient(circle at center, transparent 45%, rgba(0,0,0,0.35) 100%),
      linear-gradient(135deg, rgba(120, 90, 40, 0.85) 0%, rgba(100, 70, 30, 0.85) 50%, rgba(80, 50, 20, 0.85) 100%),
      radial-gradient(circle at 80% 20%, rgba(160, 140, 100, 0.25) 0%, transparent 65%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'black-textured',
    name: 'Black Textured',
    backgroundImage: `
      radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.25) 100%),
      linear-gradient(312deg, rgba(35, 35, 45, 0.9) 0%, rgba(25, 25, 35, 0.9) 50%, rgba(15, 15, 25, 0.9) 100%),
      radial-gradient(circle at 70% 30%, rgba(40, 35, 60, 0.15) 0%, transparent 70%),
      radial-gradient(circle at 20% 80%, rgba(60, 40, 80, 0.1) 0%, transparent 60%),
      radial-gradient(circle at 40% 60%, rgba(25, 45, 40, 0.12) 0%, transparent 80%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten, lighten, lighten',
  },
  {
    id: 'burgundy-wine',
    name: 'Burgundy Wine',
    backgroundImage: `
      radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.12) 100%),
      linear-gradient(216deg, rgba(80, 30, 50, 0.85) 0%, rgba(60, 20, 40, 0.85) 50%, rgba(40, 15, 30, 0.85) 100%),
      radial-gradient(circle at 31% 45%, rgba(140, 80, 100, 0.2) 0%, transparent 90%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'charcoal-smoke',
    name: 'Charcoal Smoke',
    backgroundImage: `
      radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.2) 100%),
      linear-gradient(312deg, rgba(50, 50, 55, 0.9) 0%, rgba(35, 35, 40, 0.9) 50%, rgba(20, 20, 25, 0.9) 100%),
      radial-gradient(circle at 31% 45%, rgba(100, 110, 120, 0.15) 0%, transparent 90%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'copper-rust',
    name: 'Copper Rust',
    backgroundImage: `
      radial-gradient(circle at center, transparent 45%, rgba(0,0,0,0.35) 100%),
      linear-gradient(135deg, rgba(140, 70, 50, 0.85) 0%, rgba(120, 60, 40, 0.85) 50%, rgba(100, 50, 30, 0.85) 100%),
      radial-gradient(circle at 80% 20%, rgba(180, 140, 120, 0.25) 0%, transparent 65%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'crimson-red',
    name: 'Crimson Red',
    backgroundImage: `
      radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.11) 100%),
      linear-gradient(216deg, rgba(104, 44, 44, 0.89) 0%, rgba(34, 36, 93, 0.36) 50%, rgba(77, 26, 26, 0.47) 100%),
      radial-gradient(circle at 31% 45%, rgba(255, 0, 174, 0.24) 0%, transparent 90%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'cyber-purple',
    name: 'Cyber Purple',
    backgroundImage: `
      radial-gradient(circle at center, transparent 0%, rgba(154, 3, 3, 0.11) 100%),
      linear-gradient(168deg, rgba(70, 70, 70, 0.39) 0%, rgba(0, 0, 0, 0.36) 50%, rgba(157, 0, 205, 0.1) 100%),
      radial-gradient(circle at 31% 45%, rgba(0, 178, 254, 0.3) 0%, transparent 90%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'forest-green',
    name: 'Forest Green',
    backgroundImage: `
      radial-gradient(circle at center, transparent 45%, rgba(0,0,0,0.4) 100%),
      linear-gradient(135deg, rgba(45, 85, 65, 0.9) 0%, rgba(35, 75, 55, 0.9) 50%, rgba(25, 55, 45, 0.9) 100%),
      radial-gradient(circle at 80% 20%, rgba(80, 120, 100, 0.3) 0%, transparent 65%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'greyscale-minimal',
    name: 'Greyscale Minimal',
    backgroundImage: `
      radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.08) 100%),
      linear-gradient(168deg, rgba(80, 80, 80, 0.4) 0%, rgba(60, 60, 60, 0.4) 50%, rgba(40, 40, 40, 0.4) 100%),
      radial-gradient(circle at 31% 45%, rgba(120, 120, 120, 0.15) 0%, transparent 90%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'grey-green',
    name: 'Grey Green',
    backgroundImage: `
      linear-gradient(168deg, rgb(125, 125, 125) 0%, rgba(73, 120, 50, 0.59) 50%, rgb(69, 69, 69) 100%)
    `,
    backgroundBlendMode: 'normal',
  },
  {
    id: 'indigo-night',
    name: 'Indigo Night',
    backgroundImage: `
      radial-gradient(circle at center, transparent 45%, rgba(0,0,0,0.4) 100%),
      linear-gradient(135deg, rgba(55, 45, 85, 0.9) 0%, rgba(45, 35, 75, 0.9) 50%, rgba(35, 25, 65, 0.9) 100%),
      radial-gradient(circle at 80% 20%, rgba(100, 90, 130, 0.3) 0%, transparent 65%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    backgroundImage: `
      radial-gradient(circle at center, transparent 45%, rgba(0,0,0,0.4) 100%),
      linear-gradient(135deg, rgba(45, 75, 105, 0.9) 0%, rgba(35, 85, 95, 0.9) 50%, rgba(25, 45, 75, 0.9) 100%),
      radial-gradient(circle at 80% 20%, rgba(90, 130, 150, 0.3) 0%, transparent 65%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'sage-mist',
    name: 'Sage Mist',
    backgroundImage: `
      radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.1) 100%),
      linear-gradient(168deg, rgba(80, 100, 85, 0.75) 0%, rgba(60, 80, 65, 0.75) 50%, rgba(40, 60, 45, 0.75) 100%),
      radial-gradient(circle at 31% 45%, rgba(140, 160, 150, 0.2) 0%, transparent 90%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'sepia-vintage',
    name: 'Sepia Vintage',
    backgroundImage: `
      radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.08) 100%),
      linear-gradient(216deg, rgba(110, 85, 65, 0.8) 0%, rgba(90, 65, 45, 0.8) 50%, rgba(70, 45, 25, 0.8) 100%),
      radial-gradient(circle at 31% 45%, rgba(160, 140, 120, 0.18) 0%, transparent 90%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'slate-grey',
    name: 'Slate Grey',
    backgroundImage: `
      radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.15) 100%),
      linear-gradient(168deg, rgba(60, 70, 80, 0.8) 0%, rgba(40, 50, 60, 0.8) 50%, rgba(20, 30, 40, 0.8) 100%),
      radial-gradient(circle at 31% 45%, rgba(120, 140, 160, 0.25) 0%, transparent 90%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'steel-blue',
    name: 'Steel Blue',
    backgroundImage: `
      radial-gradient(circle at center, transparent 45%, rgba(0,0,0,0.3) 100%),
      linear-gradient(135deg, rgba(65, 85, 95, 0.85) 0%, rgba(55, 75, 85, 0.85) 50%, rgba(45, 65, 75, 0.85) 100%),
      radial-gradient(circle at 80% 20%, rgba(120, 140, 150, 0.25) 0%, transparent 65%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'sunset-warm',
    name: 'Sunset Warm',
    backgroundImage: `
      radial-gradient(circle at center, transparent 45%, rgba(0,0,0,0.40) 100%),
      linear-gradient(135deg, rgba(104, 99, 44, 0.90) 0%, rgba(93, 49, 34, 0.90) 50%, rgba(77, 26, 26, 0.90) 100%),
      radial-gradient(circle at 80% 20%, rgba(90, 130, 150, 0.30) 0%, transparent 65%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'teal-mint',
    name: 'Teal Mint',
    backgroundImage: `
      radial-gradient(circle at center, transparent 45%, rgba(0,0,0,0.3) 100%),
      linear-gradient(135deg, rgba(50, 90, 85, 0.8) 0%, rgba(40, 80, 75, 0.8) 50%, rgba(30, 70, 65, 0.8) 100%),
      radial-gradient(circle at 80% 20%, rgba(100, 150, 140, 0.25) 0%, transparent 65%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
  {
    id: 'white-grey',
    name: 'White Grey',
    backgroundImage: `
      radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.03) 100%),
      linear-gradient(168deg, rgba(200, 200, 200, 0.3) 0%, rgba(180, 180, 180, 0.3) 50%, rgba(160, 160, 160, 0.3) 100%),
      radial-gradient(circle at 31% 45%, rgba(220, 220, 220, 0.2) 0%, transparent 90%)
    `,
    backgroundBlendMode: 'multiply, overlay, lighten',
  },
];

export type ModuleIndicatorStyle = 'dots' | 'emojis' | 'none';

export const MODULE_INDICATOR_OPTIONS: {
  id: ModuleIndicatorStyle;
  name: string;
  description: string;
}[] = [
  { id: 'dots', name: 'Colored Dots', description: 'Module color indicators' },
  { id: 'emojis', name: 'Emojis', description: 'Bucket emoji icons' },
  { id: 'none', name: 'None', description: 'Hide indicators' },
];

export class UiThemeStore {
  darkMode = true;
  selectedCardThemeId = 'ocean-blue';
  selectedFontThemeId = 'editorial';
  showCardTexture = true;
  useSerifFont = true;
  moduleIndicatorStyle: ModuleIndicatorStyle = 'dots';

  constructor() {
    makeObservable(this, {
      darkMode: observable,
      selectedCardThemeId: observable,
      selectedFontThemeId: observable,
      showCardTexture: observable,
      useSerifFont: observable,
      moduleIndicatorStyle: observable,
      toggleDarkMode: action.bound,
      setCardTheme: action.bound,
      setFontTheme: action.bound,
      toggleFont: action.bound,
      toggleCardTexture: action.bound,
      setModuleIndicatorStyle: action.bound,
      selectedCardTheme: computed,
      selectedFontTheme: computed,
      titleFontFamily: computed,
      titleFontWeight: computed,
    });
  }

  init() {
    const savedDarkMode = window.localStorage.getItem('darkMode');
    if (savedDarkMode) {
      this.darkMode = savedDarkMode === 'true';
    } else {
      // Default to dark mode for new users
      this.darkMode = true;
    }

    const savedCardTheme = window.localStorage.getItem('selectedCardThemeId');
    if (savedCardTheme && CARD_THEMES.some((theme) => theme.id === savedCardTheme)) {
      this.selectedCardThemeId = savedCardTheme;
    }

    const savedFontTheme = window.localStorage.getItem('selectedFontThemeId');
    if (savedFontTheme && FONT_THEMES.some((theme) => theme.id === savedFontTheme)) {
      this.selectedFontThemeId = savedFontTheme;
    }

    // Load compatibility settings from localStorage
    const savedUseSerifFont = window.localStorage.getItem('useSerifFont');
    if (savedUseSerifFont) {
      this.useSerifFont = savedUseSerifFont === 'true';
    }

    const savedShowCardTexture = window.localStorage.getItem('showCardTexture');
    if (savedShowCardTexture) {
      this.showCardTexture = savedShowCardTexture === 'true';
    }

    const savedModuleIndicatorStyle = window.localStorage.getItem('moduleIndicatorStyle');
    if (
      savedModuleIndicatorStyle &&
      ['dots', 'emojis', 'none'].includes(savedModuleIndicatorStyle)
    ) {
      this.moduleIndicatorStyle = savedModuleIndicatorStyle as ModuleIndicatorStyle;
    }
  }

  toggleDarkMode() {
    this.darkMode = !this.darkMode;
    window.localStorage.setItem('darkMode', `${this.darkMode}`);
  }

  setCardTheme(themeId: string) {
    if (CARD_THEMES.some((theme) => theme.id === themeId)) {
      this.selectedCardThemeId = themeId;
      window.localStorage.setItem('selectedCardThemeId', themeId);
    }
  }

  setFontTheme(themeId: string) {
    if (FONT_THEMES.some((theme) => theme.id === themeId)) {
      this.selectedFontThemeId = themeId;
      window.localStorage.setItem('selectedFontThemeId', themeId);
    }
  }

  get selectedCardTheme(): CardTheme {
    return CARD_THEMES.find((theme) => theme.id === this.selectedCardThemeId) || CARD_THEMES[0];
  }

  get selectedFontTheme(): FontTheme {
    return FONT_THEMES.find((theme) => theme.id === this.selectedFontThemeId) || FONT_THEMES[0];
  }

  // Compatibility getters for master's code
  get titleFontFamily(): string {
    return this.useSerifFont ? '"Roboto Serif", serif' : '"Roboto", sans-serif';
  }

  get titleFontWeight(): number {
    return this.useSerifFont ? 300 : 400;
  }

  toggleFont() {
    this.useSerifFont = !this.useSerifFont;
    window.localStorage.setItem('useSerifFont', `${this.useSerifFont}`);
  }

  toggleCardTexture() {
    this.showCardTexture = !this.showCardTexture;
    window.localStorage.setItem('showCardTexture', `${this.showCardTexture}`);
  }

  setModuleIndicatorStyle(style: ModuleIndicatorStyle) {
    this.moduleIndicatorStyle = style;
    window.localStorage.setItem('moduleIndicatorStyle', style);
  }
}

// Singleton instance for global access (used by designTokens)
let _uiThemeStoreInstance: UiThemeStore | null = null;

export function getUiThemeStore(): UiThemeStore {
  if (!_uiThemeStoreInstance) {
    _uiThemeStoreInstance = new UiThemeStore();
  }
  return _uiThemeStoreInstance;
}
